import * as singleSpa from "single-spa";
import type { Application } from "single-spa";
import { constructApplications, constructRoutes, constructLayoutEngine } from "single-spa-layout";
import { loadWafScript } from "@flash-tecnologia/flashstage-auth/dist/captcha/scripts.js";
import { getLogger } from "@flash-tecnologia/hros-telemetry";

import "./config/telemetry";
import microfrontendLayout from "./microfrontend-layout.html?raw";
import { checkAndRedirectToMobileDeepLink } from "./config/deeplink/mobileDeepLinkRedirect";
import { updateNavigationLayoutState } from "./config/navigation-layout";

import "./css/main.css";
import "./css/normalize.css";
import { metadataMap } from "./config/seo/metadata";
import { shouldIndexPath } from "./config/seo/robots";

const { registerApplication, start } = singleSpa;
const logger = getLogger(["hros-root", "bootstrap"]);
const ACCESS_CONTEXT_UTILITY_LOAD_TIMEOUT_MS = 3000;

type AccessContextUtility = {
  isAccessContextRevisionStorageEvent?: (event: StorageEvent) => boolean;
  reconcileAccessContext?: () => "stable" | "pending" | "reloaded";
};

function shareSingleSpaWithSystemJs() {
  const moduleId = System.resolve("single-spa");

  if (!System.has(moduleId)) {
    System.set(moduleId, singleSpa);
  }
}

shareSingleSpaWithSystemJs();

loadWafScript();

checkAndRedirectToMobileDeepLink();

const originalFetch = window.fetch.bind(window);
const flashDomains = ["flashapp.site", "flashapp.dev", "flashapp.services"];
const iproovDomains = ["latam.rp.secure.iproov.me", "cdn.iproov.app"];

function isExactHostOrSubdomain(hostname: string, domain: string) {
  const normalizedHostname = hostname.toLowerCase().replace(/\.$/u, "");
  const normalizedDomain = domain.toLowerCase();

  return (
    normalizedHostname === normalizedDomain || normalizedHostname.endsWith(`.${normalizedDomain}`)
  );
}

function isAllowedFlashHost(hostname: string) {
  return flashDomains.some((domain) => isExactHostOrSubdomain(hostname, domain));
}

function isIproovHost(hostname: string) {
  return iproovDomains.some((domain) => isExactHostOrSubdomain(hostname, domain));
}

function getRequestUrl(input: RequestInfo | URL) {
  const rawUrl =
    input instanceof Request ? input.url : input instanceof URL ? input.toString() : input;

  return new URL(rawUrl, window.location.href);
}

window.fetch = async function (input: RequestInfo | URL, config?: RequestInit) {
  const url = getRequestUrl(input);
  const isFlashHost = isAllowedFlashHost(url.hostname);
  const shouldSendDeviceId = isFlashHost;
  const shouldSendOrigin = isFlashHost && !isIproovHost(url.hostname);

  const headers = new Headers(input instanceof Request ? input.headers : undefined);
  new Headers(config?.headers).forEach((value, name) => {
    headers.set(name, value);
  });

  if (shouldSendOrigin) {
    headers.set("x-flash-origin", "flashos-web");
  }

  if (shouldSendDeviceId) {
    let deviceId = window.localStorage.getItem("x-flash-device-id");
    if (!deviceId) {
      deviceId = crypto.randomUUID();
      window.localStorage.setItem("x-flash-device-id", deviceId);
    }

    headers.set("X-FLASH-DEVICE-ID", deviceId);

    const wafIntegration = (window as any).AwsWafIntegration;
    if (wafIntegration?.hasToken?.()) {
      headers.set("x-aws-waf-token", await wafIntegration.getToken());
    }
  }

  return originalFetch(input, { ...config, headers });
};

type FetchWithCompatibilityMarker = typeof window.fetch & {
  __hrosFetchInputCompatibility?: boolean;
};

function normalizeFetchInput(input: RequestInfo | URL): RequestInfo | URL {
  if (typeof input === "string") {
    return new URL(input, window.location.href).toString();
  }

  if (input instanceof URL) {
    return input.toString();
  }

  return input;
}

function ensureFetchInputCompatibility() {
  const currentFetch = window.fetch as FetchWithCompatibilityMarker;
  if (currentFetch.__hrosFetchInputCompatibility) {
    return;
  }

  const compatibleFetch = function (input: RequestInfo | URL, config?: RequestInit) {
    return currentFetch(normalizeFetchInput(input), config);
  } as FetchWithCompatibilityMarker;

  compatibleFetch.__hrosFetchInputCompatibility = true;
  window.fetch = compatibleFetch;
}

ensureFetchInputCompatibility();

function updateMetadata() {
  const path = window.location.pathname;
  const metadata = metadataMap[path] || metadataMap["*"];

  document.title = metadata.title;

  let metaDescription = document.querySelector("meta[name='description']");
  if (!metaDescription) {
    metaDescription = document.createElement("meta");
    metaDescription.setAttribute("name", "description");
    document.head.appendChild(metaDescription);
  }
  metaDescription.setAttribute("content", metadata.description);

  let metaRobots = document.querySelector("meta[name='robots']");
  if (!shouldIndexPath(path)) {
    if (!metaRobots) {
      metaRobots = document.createElement("meta");
      metaRobots.setAttribute("name", "robots");
      document.head.appendChild(metaRobots);
    }
    metaRobots.setAttribute("content", "noindex, nofollow");
  } else {
    if (metaRobots) {
      metaRobots.remove();
    }
  }
}

updateMetadata();

window.addEventListener("single-spa:routing-event", () => {
  updateMetadata();
});

const routes = constructRoutes(microfrontendLayout);
const applications = constructApplications({
  routes,
  loadApp({ name }) {
    return System.import(name).then((application) => {
      ensureFetchInputCompatibility();
      return application as Application;
    });
  },
});

const navigationApplication = applications.find(({ name }) => name === "@flash-hros/menu");

if (navigationApplication) {
  const existingCustomProps = navigationApplication.customProps;

  navigationApplication.customProps = (name, location) => ({
    ...(typeof existingCustomProps === "function"
      ? existingCustomProps(name, location)
      : (existingCustomProps ?? {})),
    onNavigationLayoutStateChange: updateNavigationLayoutState,
  });
}

const layoutEngine = constructLayoutEngine({ routes, applications });

function loadSystemJsAmdExtension(): Promise<void> {
  if (process.env.NODE_ENV !== "development") {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "/libs/systemjs/amd.min.js";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Unable to load the SystemJS AMD extension"));
    document.head.appendChild(script);
  });
}

function loadAccessContextUtility(): Promise<AccessContextUtility> {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(new Error("Timed out while loading HROS web utility"));
    }, ACCESS_CONTEXT_UTILITY_LOAD_TIMEOUT_MS);

    System.import("@flash-tecnologia/hros-web-utility").then(
      (module) => {
        window.clearTimeout(timeoutId);
        resolve(module as AccessContextUtility);
      },
      (error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      },
    );
  });
}

const setupAccessContextReconciliation = (utility?: AccessContextUtility): boolean => {
  // Keep the root compatible while the utility remote is being rolled out.
  if (typeof utility?.reconcileAccessContext !== "function") {
    return true;
  }

  const { isAccessContextRevisionStorageEvent, reconcileAccessContext } = utility;

  let accessContextCheckRequested = false;

  const checkAccessContext = () => {
    if (document.visibilityState === "hidden") return;
    if (!accessContextCheckRequested) return;

    accessContextCheckRequested = false;
    reconcileAccessContext();
  };

  const requestAccessContextCheck = () => {
    accessContextCheckRequested = true;
    checkAccessContext();
  };

  window.addEventListener("storage", (event) => {
    if (
      typeof isAccessContextRevisionStorageEvent === "function" &&
      isAccessContextRevisionStorageEvent(event)
    ) {
      requestAccessContextCheck();
    }
  });
  window.addEventListener("focus", requestAccessContextCheck);
  window.addEventListener("pageshow", requestAccessContextCheck);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      requestAccessContextCheck();
    }
  });

  // Run before activating any microfrontend so a stale tab does not reach a
  // module-level permission guard and get redirected to /home.
  accessContextCheckRequested = true;
  const initialResult = reconcileAccessContext();
  accessContextCheckRequested = false;

  return initialResult !== "reloaded";
};

const startLayout = () => {
  applications.forEach(registerApplication);
  layoutEngine.activate();
  start();
};

const initializeApplication = async () => {
  try {
    const utility = await loadAccessContextUtility();

    if (!setupAccessContextReconciliation(utility)) {
      return;
    }
  } catch (error) {
    logger.error("Failed to initialize access context reconciliation; starting layout", {
      error,
    });
  }

  startLayout();
};

loadSystemJsAmdExtension()
  .then(initializeApplication)
  .catch((error) => {
    logger.error("Failed to initialize application", { error });
  });
